import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, inr, inrPlain, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";
import { CardSkeleton } from "@/components/Skeletons";
import { motion } from "framer-motion";
import { format } from "date-fns";
import SettlementDialog from "@/components/SettlementDialog";
import { Copy, MessageCircle, QrCode, Send, User } from "lucide-react";
import { toast } from "sonner";

export default function MemberDetails() {
  const { memberId } = useParams();
  const { user } = useAuth();
  const { group, currentRole } = useGroup();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settleOpen, setSettleOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get(`/expenses/group/${group.id}/member/${memberId}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (group?.id && memberId) load();
  }, [group?.id, memberId]);

  if (loading || !data) return <CardSkeleton count={2} />;

  const outstanding = data.net_balance || 0; // positive = other owes user, negative = user owes other
  const owed = outstanding > 0; // they owe me
  const iOwe = outstanding < 0;

  const requestPayment = async () => {
    try {
      await api.post("/payment-requests", {
        group_id: group.id,
        from_user_id: memberId,
        amount: Math.abs(outstanding),
      });
      toast.success("Payment reminder sent");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/pay?to=${user.id}&amount=${Math.abs(outstanding)}`;
    navigator.clipboard.writeText(link);
    toast.success("Payment link copied");
  };

  const shareWhatsapp = () => {
    const msg = `Hi ${data.other.name}, please transfer ₹${Math.abs(outstanding).toFixed(2)} for our shared expenses. Thanks!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-5 lg:max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full overflow-hidden ring-1 ring-slate-200 bg-slate-100 grid place-items-center shrink-0">
          {data.other.photo ? (
            <img src={data.other.photo} alt={data.other.name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-slate-400" />
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Balance with</div>
          <h1 className="heading text-2xl font-bold tracking-tight text-slate-900" data-testid="member-detail-name">{data.other.name}</h1>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-3xl p-6 border ${owed ? "bg-emerald-50 border-emerald-200" : iOwe ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}
      >
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Outstanding</div>
        <div className={`amount text-4xl font-bold mt-2 ${owed ? "text-emerald-700" : iOwe ? "text-rose-700" : "text-slate-700"}`} data-testid="member-outstanding">
          {inr(outstanding)}
        </div>
        <p className="text-sm text-slate-600 mt-1">
          {owed ? `${data.other.name} owes you` : iOwe ? `You owe ${data.other.name}` : "You are settled up"}
        </p>

        {iOwe && (
          <button
            onClick={() => setDialog(true)}
            className="mt-4 w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="member-settle-button"
          >
            Settle up
          </button>
        )}
        {owed && (
          <div className="mt-4 grid grid-cols-4 gap-2">
            <button onClick={requestPayment} className="p-3 rounded-2xl bg-white border border-slate-200 text-slate-700 active:scale-95" data-testid="member-remind-button">
              <Send className="w-4 h-4 mx-auto" />
              <div className="text-[11px] font-semibold mt-1">Remind</div>
            </button>
            <button onClick={copyLink} className="p-3 rounded-2xl bg-white border border-slate-200 text-slate-700 active:scale-95" data-testid="member-copy-link">
              <Copy className="w-4 h-4 mx-auto" />
              <div className="text-[11px] font-semibold mt-1">Link</div>
            </button>
            <button onClick={shareWhatsapp} className="p-3 rounded-2xl bg-white border border-slate-200 text-slate-700 active:scale-95" data-testid="member-share-whatsapp">
              <MessageCircle className="w-4 h-4 mx-auto" />
              <div className="text-[11px] font-semibold mt-1">WhatsApp</div>
            </button>
            <button
              onClick={() => {
                const upi = `upi://pay?pa=you@upi&pn=${encodeURIComponent(user.name)}&am=${Math.abs(outstanding)}&cu=INR`;
                window.open(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(upi)}`, "_blank");
              }}
              className="p-3 rounded-2xl bg-white border border-slate-200 text-slate-700 active:scale-95"
              data-testid="member-qr"
            >
              <QrCode className="w-4 h-4 mx-auto" />
              <div className="text-[11px] font-semibold mt-1">QR</div>
            </button>
          </div>
        )}
      </motion.div>

      <section>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Timeline</div>
        <div className="relative pl-6">
          <div className="absolute left-2.5 top-1 bottom-1 w-px bg-slate-200" />
          <div className="space-y-3">
            {data.entries.length === 0 && (
              <div className="text-sm text-slate-500 p-4 rounded-2xl bg-slate-50 border border-slate-200">
                No transactions yet.
              </div>
            )}
            {data.entries.map((e) => {
              const positive = e.signed_amount > 0;
              return (
                <div key={e.id} className="relative">
                  <div className={`absolute -left-[18px] top-4 w-3 h-3 rounded-full ring-4 ring-white ${positive ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <div className="p-4 rounded-2xl bg-white border border-slate-200">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
                          {e.transaction_type}
                        </div>
                        <div className="font-semibold text-slate-900 mt-1">{e.remarks}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {format(new Date(e.created_at), "PP • p")}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`amount font-bold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
                          {positive ? "+" : ""}{inrPlain(e.signed_amount).replace("₹-", "-₹")}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Running {inrPlain(e.running_balance)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SettlementDialog
        open={dialog}
        onOpenChange={setDialog}
        groupId={groupId}
        payerId={user.id}
        receiverId={memberId}
        outstanding={Math.abs(outstanding)}
        onSuccess={load}
      />
    </div>
  );
}
